import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  purchaseOrdersService,
  PurchaseOrderFilters,
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
  ReceiveRequest,
  SupplierPaymentCreate,
} from '@/services/purchaseOrders';

export function usePurchaseOrders(filters?: PurchaseOrderFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['purchase-orders', filters],
    queryFn: () => purchaseOrdersService.getPurchaseOrders(filters),
    enabled: options?.enabled ?? true,
  });
}

export function usePurchaseOrder(id: number) {
  return useQuery({
    queryKey: ['purchase-orders', id],
    queryFn: () => purchaseOrdersService.getPurchaseOrder(id),
    enabled: !!id,
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PurchaseOrderCreate) => purchaseOrdersService.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PurchaseOrderUpdate }) =>
      purchaseOrdersService.updatePurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useEditPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PurchaseOrderCreate }) =>
      purchaseOrdersService.editPurchaseOrder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => purchaseOrdersService.cancelPurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => purchaseOrdersService.deletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useReceivePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ poId, data }: { poId: number; data: ReceiveRequest }) =>
      purchaseOrdersService.receiveItems(poId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useRegisterSupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, data }: { supplierId: number; data: SupplierPaymentCreate }) =>
      purchaseOrdersService.registerSupplierPayment(supplierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'debt-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-payments'] });
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useAdjustSupplierDebt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, data }: { supplierId: number; data: { amount: number; direction: 'reduce' | 'increase'; reason: string } }) =>
      purchaseOrdersService.adjustSupplierDebt(supplierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'debt-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-payments'] });
    },
  });
}

export function useSupplierPayments(supplierId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['supplier-payments', supplierId],
    queryFn: () => purchaseOrdersService.getSupplierPayments(supplierId),
    enabled: (options?.enabled ?? true) && !!supplierId,
  });
}

export function useUpdateSupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: {
      paymentId: number;
      data: { amount?: number; payment_method?: string; payment_date?: string; reference?: string | null; notes?: string | null };
    }) => purchaseOrdersService.updateSupplierPayment(paymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'debt-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-payments'] });
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useDeleteSupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: number) =>
      purchaseOrdersService.deleteSupplierPayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'debt-summary'] });
      queryClient.invalidateQueries({ queryKey: ['supplier-payments'] });
      queryClient.invalidateQueries({ queryKey: ['cash-register'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'summary'] });
    },
  });
}

export function useSupplierDebtSummary(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['purchase-orders', 'debt-summary'],
    queryFn: () => purchaseOrdersService.getDebtSummary(),
    enabled: options?.enabled ?? true,
  });
}
